import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../../services/adminApiService';
import { realtimeDbService } from '../../../services/realtimeDbService';
import { ShieldCheck, Search, RefreshCw, FileText, User } from 'lucide-react';
import { formatDateTR } from '../../../utils/dateUtils';

export const AdminAuditLogsView: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadLogs = async () => {
    setLoading(true);
    try {
      const [apiRes, rtLogs] = await Promise.all([
        adminApiService.getLogs().catch(() => ({ success: false, logs: [] })),
        realtimeDbService.getAdminAuditLogs().catch(() => []),
      ]);

      const combined: any[] = [];

      if (apiRes.success && Array.isArray(apiRes.logs)) {
        combined.push(...apiRes.logs);
      }

      if (Array.isArray(rtLogs)) {
        for (const r of rtLogs) {
          combined.push({
            logId: r.id,
            timestamp: r.createdAt,
            timestampFormatted: formatDateTR(r.createdAt),
            action: r.action,
            userId: r.adminEmail || r.adminUid || 'Super Admin',
            siteId: r.tenantId,
            details: `${r.product} (${r.oldStatus || '-'} ➔ ${r.newStatus}) | Gerekçe: ${r.reason || '-'} | Müşteri: ${r.customer || r.tenantId}`,
          });
        }
      }

      // Sort by newest timestamp
      combined.sort((a, b) => {
        const timeA = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
        const timeB = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
        return timeB - timeA;
      });

      setLogs(combined);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const filtered = logs.filter(
    (l) =>
      l.action?.toLowerCase().includes(search.toLowerCase()) ||
      l.userId?.toLowerCase().includes(search.toLowerCase()) ||
      l.siteId?.toLowerCase().includes(search.toLowerCase()) ||
      l.details?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-rose-600" />
            <span>Sistem Denetim & İşlem Kayıtları (Audit Logs)</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Platformda gerçekleşen tüm kritik işlemleri, admin güncellemelerini ve güvenlik denetim loglarını inceleyin.
          </p>
        </div>
        <button
          onClick={loadLogs}
          disabled={loading}
          className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-2 self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="İşlem türü, kullanıcı ID, site ID veya detay ile ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-rose-500/20 bg-white"
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Tarih</th>
                <th className="py-3 px-4">İşlem Türü</th>
                <th className="py-3 px-4">Kullanıcı / Yetkili</th>
                <th className="py-3 px-4">Hedef Site ID</th>
                <th className="py-3 px-4">Detay</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-rose-500" />
                    Denetim kayıtları yükleniyor...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400">
                    Kayıtlı log bulunamadı.
                  </td>
                </tr>
              ) : (
                filtered.map((l, i) => (
                  <tr key={l.logId || i} className="hover:bg-slate-50/70 transition">
                    <td className="py-3.5 px-4 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                      {l.timestampFormatted || new Date(l.timestamp).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold text-slate-900">
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-800 text-[11px]">
                        {l.action}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-slate-600 text-[11px]">
                      {l.userId || 'Sistem'}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-slate-500 text-[11px]">
                      {l.siteId || '-'}
                    </td>
                    <td className="py-3.5 px-4 text-slate-700 max-w-md">
                      {l.details}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
